/**
 * TradingPatterns — Displays buy/sell ratio, trade frequency, and average trade size.
 *
 * Derives all metrics client-side from existing trade history data.
 * No new API endpoints — uses useTradeHistory from Epic 4.
 *
 * Story 8.5 — Task 1
 */

import { useTradeHistory } from "../../hooks/api/use-trade-history";
import { computeTradingPatterns } from "../../lib/compute-trading-patterns";
import { formatTokenAmount } from "../../lib/format-number";
import { getNativeCurrencySymbol } from "../../lib/wagmi-config";

type TradingPatternsProps = {
	tokenId: string;
};

export function TradingPatterns({ tokenId }: TradingPatternsProps) {
	const { data, isLoading } = useTradeHistory(tokenId);

	if (isLoading) {
		return <TradingPatternsSkeleton />;
	}

	const trades = data?.data ?? [];
	const metrics = computeTradingPatterns(trades);
	const symbol = getNativeCurrencySymbol();

	if (metrics.totalTrades === 0) {
		return (
			<div className="rounded-xl border border-pastel-border bg-white p-5">
				<h4 className="text-sm font-semibold text-pastel-black mb-3">Trading Patterns</h4>
				<p className="text-sm text-pastel-gray">No trade data available</p>
			</div>
		);
	}

	return (
		<div className="rounded-xl border border-pastel-border bg-white p-5">
			<h4 className="text-sm font-semibold text-pastel-black mb-4">Trading Patterns</h4>

			<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
				{/* Buy/Sell Ratio */}
				<div>
					<p className="text-xs font-medium text-pastel-gray uppercase tracking-wide mb-2">
						Buy / Sell Ratio
					</p>
					<div className="flex h-3 rounded-full overflow-hidden bg-pastel-light-gray mb-1.5">
						<div
							className="bg-green-500 transition-all"
							style={{ width: `${String(metrics.buyPercentage)}%` }}
						/>
						<div
							className="bg-red-400 transition-all"
							style={{ width: `${String(metrics.sellPercentage)}%` }}
						/>
					</div>
					<p className="text-xs text-pastel-gray">
						<span className="text-green-600 font-medium">Buy {metrics.buyPercentage}%</span>
						{" / "}
						<span className="text-red-500 font-medium">Sell {metrics.sellPercentage}%</span>
					</p>
				</div>

				{/* Trade Frequency */}
				<div>
					<p className="text-xs font-medium text-pastel-gray uppercase tracking-wide mb-2">
						Trade Frequency
					</p>
					<p className="text-lg font-bold text-pastel-black">{metrics.tradesLast24h}</p>
					<p className="text-xs text-pastel-gray">trades in 24h ({metrics.tradesPerHour}/hr avg)</p>
				</div>

				{/* Average Trade Size */}
				<div>
					<p className="text-xs font-medium text-pastel-gray uppercase tracking-wide mb-2">
						Avg Trade Size
					</p>
					<p className="text-lg font-bold text-pastel-black">
						{metrics.averageTradeSize >= 0.01
							? metrics.averageTradeSize.toFixed(4)
							: metrics.averageTradeSize.toFixed(6)}{" "}
						{symbol}
					</p>
					<p className="text-xs text-pastel-gray">
						{formatTokenAmount(metrics.averageTradeSize)} {symbol} across {metrics.totalTrades}{" "}
						trades
					</p>
				</div>
			</div>
		</div>
	);
}

function TradingPatternsSkeleton() {
	return (
		<div className="rounded-xl border border-pastel-border bg-white p-5 animate-pulse">
			<div className="h-4 w-32 bg-pastel-light-gray rounded mb-4" />
			<div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
				{Array.from({ length: 3 }).map((_, i) => (
					<div key={`pattern-skeleton-${String(i)}`}>
						<div className="h-3 w-20 bg-pastel-light-gray rounded mb-2" />
						<div className="h-6 w-16 bg-pastel-light-gray rounded mb-1" />
						<div className="h-3 w-24 bg-pastel-light-gray rounded" />
					</div>
				))}
			</div>
		</div>
	);
}
